import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle, User, Users } from 'lucide-react';
import { useRef } from 'react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { partnershipsApiService } from '../../services/partnershipsApi';
import { CreatorType } from '../../types/partnership';
import { cn } from '../../utils/cn';

interface CreateCreatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (id?: string) => void;
}

export const CreateCreatorModal = ({ isOpen, onClose, onSuccess }: CreateCreatorModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [name, setName] = useState('');
  const [type, setType] = useState<CreatorType>('individual');
  const [contactInfo, setContactInfo] = useState('');
  const [website, setWebsite] = useState('');
  const [notes, setNotes] = useState('');
  const [platformCurseforge, setPlatformCurseforge] = useState('');
  const [platformModrinth, setPlatformModrinth] = useState('');
  const [platformGithub, setPlatformGithub] = useState('');
  const [platformDiscord, setPlatformDiscord] = useState('');
  const [platformTwitter, setPlatformTwitter] = useState('');
  const [platformYoutube, setPlatformYoutube] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    const platformLinks: Record<string, string> = {};
    if (platformCurseforge.trim()) platformLinks.curseforge = platformCurseforge.trim();
    if (platformModrinth.trim()) platformLinks.modrinth = platformModrinth.trim();
    if (platformGithub.trim()) platformLinks.github = platformGithub.trim();
    if (platformDiscord.trim()) platformLinks.discord = platformDiscord.trim();
    if (platformTwitter.trim()) platformLinks.twitter = platformTwitter.trim();
    if (platformYoutube.trim()) platformLinks.youtube = platformYoutube.trim();

    setIsSubmitting(true);
    try {
      const result = await partnershipsApiService.createCreator({
        name: name.trim(),
        type,
        contact_info: contactInfo.trim() || undefined,
        platform_links: Object.keys(platformLinks).length > 0 ? JSON.stringify(platformLinks) : undefined,
        website: website.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      onSuccess(result.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create creator');
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div ref={modalRef} className="modal-panel max-w-lg">
        <button onClick={onClose} className="absolute top-3 right-3 text-glass-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-semibold tracking-tight text-white mb-4">New Creator / Team</h2>

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-4">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <span className="text-sm text-red-300">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="text-sm text-glass-300 mb-1 block">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="glass-input w-full text-sm"
              placeholder="Creator or team name"
              required
            />
          </div>

          {/* Type */}
          <div>
            <label className="text-sm text-glass-300 mb-1 block">Type *</label>
            <div className="flex gap-2">
              {([
                { value: 'individual' as const, label: 'Individual', icon: User },
                { value: 'team' as const, label: 'Team', icon: Users },
              ]).map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setType(value)}
                  className={cn(
                    'glass-button-secondary flex-1 py-2 flex items-center justify-center gap-2',
                    type === value && 'bg-accent-500/15 text-accent-300 border-accent-500/25'
                  )}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <label className="text-sm text-glass-300 mb-1 block">Contact Info</label>
            <textarea
              value={contactInfo}
              onChange={(e) => setContactInfo(e.target.value)}
              className="glass-input w-full text-sm"
              placeholder="Email, Discord, etc."
              rows={2}
            />
          </div>

          {/* Website */}
          <div>
            <label className="text-sm text-glass-300 mb-1 block">Website</label>
            <input
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              className="glass-input w-full text-sm"
              placeholder="https://..."
            />
          </div>

          {/* Platform Links */}
          <div>
            <label className="text-sm text-glass-300 mb-2 block">Platform Links</label>
            <div className="space-y-2">
              {([
                { key: 'curseforge', label: 'CurseForge', value: platformCurseforge, set: setPlatformCurseforge },
                { key: 'modrinth', label: 'Modrinth', value: platformModrinth, set: setPlatformModrinth },
                { key: 'github', label: 'GitHub', value: platformGithub, set: setPlatformGithub },
                { key: 'discord', label: 'Discord', value: platformDiscord, set: setPlatformDiscord },
                { key: 'twitter', label: 'Twitter/X', value: platformTwitter, set: setPlatformTwitter },
                { key: 'youtube', label: 'YouTube', value: platformYoutube, set: setPlatformYoutube },
              ]).map(({ key, label, value, set }) => (
                <div key={key} className="flex items-center gap-2">
                  <span className="text-xs text-glass-400 w-20 flex-shrink-0">{label}</span>
                  <input
                    type="url"
                    value={value}
                    onChange={(e) => set(e.target.value)}
                    className="glass-input flex-1 text-xs py-1.5"
                    placeholder={`https://${key}.com/...`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-sm text-glass-300 mb-1 block">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="glass-input w-full text-sm"
              placeholder="Internal notes about this creator/team..."
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="glass-button-secondary" disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="glass-button" disabled={isSubmitting}>
              {isSubmitting ? <Loader className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Creator
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
